import {
  DatePicker as ChakraDatePicker,
  InputGroup,
  Portal,
  parseDate,
  useFieldContext,
} from '@chakra-ui/react';
import type { DatePickerValueChangeDetails, DateValue } from '@chakra-ui/react';
import { PortalContainerContext } from '@voly/ui-components/src/v2/providers/chakra';
import type { KeyboardEvent, ReactElement } from 'react';
import { useContext, useState } from 'react';
import { LuCalendar } from 'react-icons/lu';
import { translate } from 'src/app/project/features/translate/translate';
import { DEFAULT_DATE_FORMAT } from 'src/app/project/modules/preferences/default-date-format';
import { EDateFormat } from 'src/app/project/shared/enums/date-format.enum';
import { formatDateValue } from '../../utils/dateValue';
import FieldLockedIcon from './FieldLockedIcon';

export type TDatePickerSize = 'xs' | 'sm' | 'md' | 'lg';

export type TDatePickerSelectionMode = 'single' | 'range';

export type TDatePickerProps = {
  selectionMode?: TDatePickerSelectionMode;
  values: (string | null)[];
  onValuesChange: (isoDates: (string | null)[]) => void;
  placeholders?: string[];
  dateFormat?: EDateFormat;
  readOnly?: boolean;
  disabled?: boolean;
  locked?: boolean;
  size?: TDatePickerSize;
  id?: string;
  min?: string;
  max?: string;
};

const LOCALE = 'en-GB';
const CALENDAR_VIEWS = ['day', 'month', 'year'] as const;
const FIRST_INPUT = 0;

type TCalendarView = (typeof CALENDAR_VIEWS)[number];

const INLINE_TRIGGER_STYLE = { position: 'static', transform: 'none' };

const INPUT_TEXT_STYLE: Record<TDatePickerSize, string> = {
  xs: 'xs',
  sm: 'sm',
  md: 'sm',
  lg: 'md',
};

const toDateValue = (isoDate: string | null | undefined): DateValue | undefined =>
  isoDate ? parseDate(isoDate) : undefined;

const isDateValue = (date: DateValue | undefined): date is DateValue => date !== undefined;

const OPEN_KEYS = ['Enter', ' '];

const CalendarTable = ({ view }: { view: TCalendarView }): ReactElement => {
  if (view === 'month') {
    return <ChakraDatePicker.MonthTable />;
  }
  if (view === 'year') {
    return <ChakraDatePicker.YearTable />;
  }
  return <ChakraDatePicker.DayTable />;
};

const DatePicker = ({
  selectionMode = 'single',
  values,
  onValuesChange,
  placeholders = [translate('select_date')],
  dateFormat = DEFAULT_DATE_FORMAT,
  readOnly: readOnlyProp = false,
  disabled = false,
  locked = false,
  size = 'md',
  id,
  min,
  max,
}: TDatePickerProps): ReactElement => {
  const portalContainerRef = useContext(PortalContainerContext);
  const field = useFieldContext();
  const [open, setOpen] = useState(false);
  const inputId = id ?? field?.ids.control;
  const selected = values.map(toDateValue).filter(isDateValue);
  const readOnly = locked || readOnlyProp;
  const canOpen = !disabled && !readOnly;
  const canClear = selected.length > 0 && canOpen;

  const openCalendar = (): void => {
    if (canOpen) {
      setOpen(true);
    }
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (!OPEN_KEYS.includes(event.key)) {
      return;
    }
    event.preventDefault();
    openCalendar();
  };

  const parseShownDate = (shown: string): DateValue | undefined =>
    selected.find((date) => formatDateValue(date, dateFormat) === shown);

  const onValueChange = (details: DatePickerValueChangeDetails): void => {
    const next = values.map((_, index) => details.value[index]?.toString() ?? null);
    const unchanged = next.every((isoDate, index) => isoDate === values[index]);
    if (unchanged) {
      return;
    }
    onValuesChange(next);
  };

  const inputs = placeholders.map((placeholder, index) => (
    <ChakraDatePicker.Input
      key={index}
      index={index}
      id={index === FIRST_INPUT ? inputId : undefined}
      aria-label={index === FIRST_INPUT ? undefined : placeholder}
      readOnly
      fixOnBlur={false}
      placeholder={locked ? '' : placeholder}
      textStyle={INPUT_TEXT_STYLE[size]}
      borderRadius="md"
      cursor={canOpen ? 'pointer' : undefined}
      onClick={openCalendar}
      onKeyDown={onInputKeyDown}
    />
  ));
  const lockedInputs = inputs.map((input, index) => (
    <InputGroup
      key={index}
      flex="1"
      minW="0"
      startElement={index === FIRST_INPUT ? <FieldLockedIcon /> : undefined}
    >
      {input}
    </InputGroup>
  ));

  return (
    <ChakraDatePicker.Root
      selectionMode={selectionMode}
      value={selected}
      min={toDateValue(min)}
      max={toDateValue(max)}
      open={open}
      onOpenChange={(details) => setOpen(details.open)}
      format={(date) => formatDateValue(date, dateFormat)}
      parse={parseShownDate}
      onValueChange={onValueChange}
      disabled={disabled}
      readOnly={readOnly}
      size={size}
      colorPalette="primary"
      locale={LOCALE}
      positioning={{ strategy: 'fixed' }}
    >
      <ChakraDatePicker.Control>
        {locked ? lockedInputs : inputs}
        <ChakraDatePicker.IndicatorGroup>
          {canClear && <ChakraDatePicker.ClearTrigger />}
          <ChakraDatePicker.Trigger css={INLINE_TRIGGER_STYLE}>
            <LuCalendar />
          </ChakraDatePicker.Trigger>
        </ChakraDatePicker.IndicatorGroup>
      </ChakraDatePicker.Control>
      <Portal container={portalContainerRef ?? undefined}>
        <ChakraDatePicker.Positioner>
          <ChakraDatePicker.Content colorPalette="primary">
            {CALENDAR_VIEWS.map((view) => (
              <ChakraDatePicker.View key={view} view={view}>
                <ChakraDatePicker.Header />
                <CalendarTable view={view} />
              </ChakraDatePicker.View>
            ))}
          </ChakraDatePicker.Content>
        </ChakraDatePicker.Positioner>
      </Portal>
    </ChakraDatePicker.Root>
  );
};

export default DatePicker;
